import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { ProxiedRoutes, QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import { PRO_SLACK_COMMUNITY_INVITE_LINK } from "@lifeat/shared/user/UserConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import DiscreteTextButton from "@common/LifeAtButton/Variants/DiscreteTextButton";
import SpecialLifeAtButton from "@common/LifeAtButton/Variants/SpecialLifeAtButton";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import LifeAtModal, { ModalNotification } from "@common/LifeAtModal/LifeAtModal";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import UserContext from "@contexts/UserContext";
import { auth } from "@firebase-local/firebase";
import UserIcon from "@icons/UserIcon.svg";
import Styles from "@themes/Styles";
import { StartUserAuthFlowEvent, UserLogoutEvent } from "@user/AmplitudeUserEvents";
import DeleteAccountBlock from "@user/components/UserAccountModal/AccountModalComponents/DeleteAccountBlock";
import SubscriptionTierBlock from "@user/components/UserAccountModal/AccountModalComponents/SubscriptionTierBlock";
import {
    CLEAR_MODAL_NOTIFICATIONS_DELAY,
    CREATE_ACCOUNT_BUTTON_TEXT,
    FIREBASE_NON_EMAIL_AUTH_PROVIDER_IDS,
    MANAGE_BILLING_BTN_LABEL,
    MANAGE_PRO_SUBSCRIPTION_BTN_LABEL,
    MANAGE_PRO_SUB_PAYMENT_METHODS_BTN_LABEL,
    MODAL_BUTTON_ACCOUNT_LABEL,
    MODAL_HEADER_SIGN_IN_LABEL,
    MODAL_HEADER_SIGN_UP_LABEL,
    MODAL_HEADER_USER_SETTINGS_LABEL,
    RESEND_VERIFICATION_EMAIL_BTN_LABEL,
    USER_ACCOUNT_MODAL_ERROR_MESSAGES,
    USER_LOG_IN_BUTTON_LABEL,
    USER_LOG_OUT_BUTTON_LABEL,
    USER_SETTINGS_LABELS,
} from "@user/components/UserAccountModal/UserAccountModalConstants";
import { buildRoute } from "@util/RouteUtil";
import { BrowserUtil } from "@util/index";
import {
    sendUserVerificationEmail,
    setAmplitudeUserAccountProperties,
    updateUserData,
} from "../../service/UserService";
import { AuthForm } from "../AuthForm";
import UserSettingItem from "../UserSettings/UserSettingItem";
import { ResetPasswordButton } from "./AccountModalComponents/ResetPasswordButton";
import StripeSubscriptionUpgradeButtons from "./AccountModalComponents/StripeSubscriptionUpgradeButtons";
import UserCustomAvi from "./AccountModalComponents/UserCustomAvi";
import UserDataFields from "./AccountModalComponents/UserDataFields";
import { UserDisplayNameSetting } from "./AccountModalComponents/UserDisplayNameSetting";
import { UserEmailSetting } from "./AccountModalComponents/UserEmailSetting";
import "./UserAccountModal.scss";

const UserAccountModalNotifications: { [id: string]: ModalNotification } = {
    DataSaved: { type: "info", message: "Change Saved" },
    InvalidDisplayName: { type: "error", message: "Your user name should be between 2 and 31 characters" },
    PasswordResetTriggered: {
        type: "warn",
        message: "An email with instructions to reset your password was just sent to you.",
    },
    ErrorSavingData: {
        type: "error",
        message: "There was an error saving your change. Please refresh the page and try again, or contact support.",
    },
};

const MissingUserDataFieldNotifications: { [id: string]: ModalNotification } = {
    MissingUserType: { type: "info", message: "This will help us craft the best experience for you." },
    MissingBirthday: {
        type: "info",
        message: "Please add your birthday for special offers and an exciting birthday surprise 🎂",
    },
    MissingPhoneNumber: {
        type: "info",
        message: "Please include your phone number for account verification purposes 📲",
    },
};

const StyledUserIcon = styled(UserIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

interface UserAccountModalProps {
    // If provided indicates a new user is signing up for an account with
    // referral code for another user.
    readonly referralCode?: string;
    readonly buttonText?: string;
    readonly isSpecial?: boolean;
    readonly onLoginCallback?: () => void;
    readonly hideUserIcon?: boolean;
    readonly forceOpenModal?: boolean;
    readonly preventClose?: boolean;
    readonly onLogoutCallback?: () => void;
    readonly contentsOnly?: boolean;
    readonly hideSubscriptionContent?: boolean;
    readonly buttonInDropdown?: boolean;
    /**
     * If the logout and delete account buttons should be hidden.
     */
    readonly preventLogout?: boolean;
    /**
     * Where to redirect the user to on successful sign in (uses redirectSuccess query parameter).
     */
    readonly onSuccessRedirectUrl?: string;
}

export default function UserAccountModal(props: UserAccountModalProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const history = useHistory();

    const context = useContext(UserContext);
    const { lightMode }: AppearanceContextType = useAppearanceContext();

    const [showLoginForm, setShowLoginForm] = useState<boolean>(true);

    const [prevClose, setPrevClose] = useState(props.preventClose ?? false);
    useEffect(() => {
        if (props.preventClose && !prevClose) {
            setPrevClose(true);
        }
    }, [props.preventClose]);

    // Everything to do on component mount.
    useEffect(() => {
        // If there is a user signed in already, get the data.
        if (context.user) {
            setShowLoginForm(false);

            setUserId(context.user.uid);

            GA4Service.setUserId(context.user.uid);
            amplitudeService.setUserId(context.user.uid);
            setAmplitudeUserAccountProperties(context.user.email, context.user.metadata.creationTime);
        }
        return undefined;
    }, [context.user]);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(props.forceOpenModal ?? false);
    useEffect(() => {
        if (isModalOpen && context.user && !context.user.emailVerified) {
            const verifyEmailNotification: ModalNotification = {
                type: "warn",
                message: USER_ACCOUNT_MODAL_ERROR_MESSAGES.SignIn_EmailNotVerified,
            };
            setModalNotifications([verifyEmailNotification]);
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (props.forceOpenModal && !isModalOpen) {
            setIsModalOpen(true);
        }
    }, [props.forceOpenModal]);

    const [modalNotifications, setModalNotifications] = useState<ModalNotification[]>([]);

    const [shakeUserTypeField, setShakeUserTypeField] = useState(false);
    useEffect(() => {
        if (context.userData) {
            setShakeUserTypeField(!context.userData.userType);
            setPrevClose(!context.userData.userType);
            if (!context.userData.userType) {
                setModalNotifications([MissingUserDataFieldNotifications.MissingUserType]);
            }
        }
    }, [context.userData]);

    const clearModalNotifications = () => {
        setTimeout(() => setModalNotifications([]), CLEAR_MODAL_NOTIFICATIONS_DELAY);
    };

    const [showAsCreateAccount, setShowAsCreateAccount] = useState(false);
    useEffect(() => {
        if (isModalOpen && showLoginForm) {
            if (showAsCreateAccount) {
                // We do a timeout so that there is a delay between the modal being
                // displayed (triggered via async state change) and us grabbing the buttons
                // from the UI; otherwise, the querySelectorAll would return 0 elements.
                setTimeout(() => {
                    const els = document.querySelectorAll(".firebaseui-idp-text-long");
                    els.forEach((val, key) => {
                        const newText = val.textContent.replace("Sign in", "Sign up");
                        val.textContent = newText;
                    });
                }, 100);
            } else {
                setTimeout(() => {
                    const els = document.querySelectorAll(".firebaseui-idp-text-long");
                    els.forEach((val, key) => {
                        const newText = val.textContent.replace("Sign up", "Sign in");
                        val.textContent = newText;
                    });
                }, 100);
            }
        }
    }, [showAsCreateAccount, isModalOpen, showLoginForm]);

    // TODO: update logic in this component to only set this to true if the actual data inside the
    //  form is changed (by comparing snapshots), not just if something was toggled
    //  (e.g. toggle off, toggle on, then close modal --> should not send update call to DB)
    const [userDataModified, setUserDataModified] = useState<boolean>(false);

    const [userId, setUserId] = useState<string>(null);

    // Update the data for the user in RealTime Database. Also updates the user data
    // state variable.
    const updateUserDataForUid = (uid: string, updatedUserData: UserEntity): void => {
        updateUserData(uid, updatedUserData)
            .then(() => {
                // Successfully updated data in DB
            })
            .catch(() => {
                const updateUserDataError: ModalNotification = {
                    type: "error",
                    message: USER_ACCOUNT_MODAL_ERROR_MESSAGES.UserSettingsUpdateError,
                };
                setModalNotifications([...modalNotifications, updateUserDataError]);
            });
    };

    const userDisplayNameSetting = (
        <UserDisplayNameSetting
            onNameChangeCallback={() => setModalNotifications([UserAccountModalNotifications.DataSaved])}
            errorCallback={() => setModalNotifications([UserAccountModalNotifications.InvalidDisplayName])}
        />
    );

    const userDisplayNameInfo = context.user ? (
        <UserSettingItem
            key={`user-account-modal-user-display-name`}
            label={USER_SETTINGS_LABELS.DisplayName}
            inputComponent={userDisplayNameSetting}
        />
    ) : null;

    const userEmailInfo = context.user ? (
        <UserSettingItem
            key={`user-account-modal-user-email`}
            label={USER_SETTINGS_LABELS.UserEmail}
            inputComponent={<UserEmailSetting />}
        />
    ) : null;

    const userDataFields = (
        <span
            key={`user-data-fields-inputs`}
            className={`user-modal-user-data-field ${shakeUserTypeField ? "animated shake" : ""}`}
        >
            <UserDataFields
                flagDataUpdated={() => setUserDataModified(true)}
                closeModalOnChange={() => setIsModalOpen(false)}
                clearErrors={() => setModalNotifications([])}
            />
        </span>
    );

    // TODO: add this back in when toggling will update the user Contacts Unsubscribe Group
    //      status properly (in SendGrid) via API
    // const userEmailsOptInSetting = (context.userData) ? (
    //     <UserSettingItem
    //         key={`user-account-modal-user-opt-in-emails`}
    //         label={USER_SETTINGS_LABELS.OptInEmails}
    //         inputType={"checkbox"}
    //         inputValue={context.userData.optIn}
    //         inputDefaultValue={false}
    //         handleChange={handleOptInEmailsToggle}
    //     />
    // ) : null;

    const saveUserData = () => {
        if (context.user && context.userData && userDataModified) {
            updateUserDataForUid(userId, context.userData);
            setUserDataModified(false);
        }
    };

    const saveDataButton = (
        <span className={`user-account-button save`} key={`user-account-modal-save-changes-button`}>
            <LifeAtButton text={"Save"} isLink={false} handleClick={saveUserData} />
        </span>
    );

    const logUserOut = () => {
        if (!props.preventLogout) {
            auth.signOut().then(() => {
                GA4Service.unsetUserId();
                amplitudeService.unsetUserId();
                amplitudeService.logEvent(new UserLogoutEvent());

                window.$crisp.push(["do", "session:reset"]);

                setModalNotifications([]);
                setUserId(null);
                setUserDataModified(false);

                setShowLoginForm(true);
                setIsModalOpen(true);

                context.setUser(null);
                context.setUserData(null);

                if (window.todesktop) {
                    BrowserUtil.redirectTo(buildRoute(Routes.App));
                } else {
                    BrowserUtil.redirectTo(ProxiedRoutes.Landing);
                }

                if (props.onLogoutCallback) {
                    props.onLogoutCallback();
                }
            });
        }
    };

    const resendVerificationEmailBtn = (
        <span
            className={`user-account-button resend-verify-email`}
            key={`user-account-modal-resend-verification-email-btn`}
        >
            <LifeAtButton
                text={RESEND_VERIFICATION_EMAIL_BTN_LABEL}
                isLink={false}
                handleClick={() => {
                    sendUserVerificationEmail(context.user, props.onSuccessRedirectUrl).then(() => {
                        const verifyEmailNotification: ModalNotification = {
                            type: "warn",
                            message: USER_ACCOUNT_MODAL_ERROR_MESSAGES.SignIn_EmailVerificationSent,
                        };
                        setModalNotifications([...modalNotifications, verifyEmailNotification]);
                        setTimeout(() => setModalNotifications([]), 5000);
                    });
                }}
            />
        </span>
    );

    const logoutButton = !props.preventLogout && context.user != null && (
        <span key={`user-account-modal-logout-button`} className={`user-account-button discrete`}>
            <DiscreteTextButton label={USER_LOG_OUT_BUTTON_LABEL} onClick={logUserOut} />
        </span>
    );

    const deleteAccountButton = !props.preventLogout && context.user != null && (
        <span key={`user-account-modal-delete-acct-button`} className={`user-account-button discrete`}>
            <DeleteAccountBlock />
        </span>
    );

    // Only show password reset button for users that created accounts via email/password.
    const resetPasswordBtn =
        context.user != null &&
        FIREBASE_NON_EMAIL_AUTH_PROVIDER_IDS.includes(context.user.providerData[0].providerId) ? null : (
            <span key={"user-account-modal-reset-pwd-btn"} className={`user-account-button discrete`}>
                <ResetPasswordButton
                    callback={() =>
                        setModalNotifications([
                            ...modalNotifications,
                            UserAccountModalNotifications.PasswordResetTriggered,
                        ])
                    }
                />
            </span>
        );

    // If the user's data has been fetched, we display the settings/information.
    let userSettingsElements: JSX.Element[] = [];
    if (context.user) {
        userSettingsElements.push(...[<UserCustomAvi key={"user-custom-avi"} />]);

        userSettingsElements.push(...[userEmailInfo, userDisplayNameInfo]);

        // Only show user data fields if user data exists
        if (context.userData) {
            userSettingsElements.push(...[userDataFields]);
        }

        // Only show resend email button if user still needs to verify email
        if (!context.user.emailVerified) {
            userSettingsElements.push(...[resendVerificationEmailBtn]);
        }

        const proUserInfo = context.userData?.proUserInfo;
        if (!props.hideSubscriptionContent) {
            // If the user is not a Pro user or doesn't have Stripe info stored,
            // we show the Upgrade button.
            if (context.userData && (!proUserInfo?.isProUser || !proUserInfo?.stripeInfo)) {
                userSettingsElements.push(
                    <StripeSubscriptionUpgradeButtons key={"user-account-modal-stripe-upgrade-btn"} isVisible={true} />,
                );
            }

            if (proUserInfo?.stripeInfo) {
                userSettingsElements.push(
                    <SubscriptionTierBlock
                        key={"user-account-modal-stripe-portal-btn"}
                        isVisible={true}
                        hideLabel={!proUserInfo.isProUser}
                    />,
                );

                if (proUserInfo?.isProUser) {
                    // If user is active subscriber, show button to take them to
                    // cancel subscription page.
                    userSettingsElements.push(
                        <span key={"user-account-modal-cancel-sub-btn"} className={`user-account-button discrete`}>
                            <DiscreteTextButton
                                label={MANAGE_PRO_SUBSCRIPTION_BTN_LABEL}
                                onClick={() => BrowserUtil.openPage(`/${Routes.ManageAccount}`, false)}
                            />
                        </span>,
                    );

                    userSettingsElements.push(
                        <span
                            key={"user-account-modal-manage-payment-methods-btn"}
                            className={`user-account-button discrete`}
                        >
                            <DiscreteTextButton
                                label={MANAGE_PRO_SUB_PAYMENT_METHODS_BTN_LABEL}
                                onClick={() => history.push(`/${Routes.ManageSubscription}`)}
                            />
                        </span>,
                    );
                } else {
                    // If user has Stripe data and is not actively a Pro user, show
                    // button to take them to /manage page to be able to access the
                    // Stripe billing portal. We don't want to show this to active
                    // users because we want to ensure they cancel their subscription
                    // by going to the /subscription-cancel page.
                    userSettingsElements.push(
                        <span key={"user-account-modal-manage-billing-btn"} className={`user-account-button discrete`}>
                            <DiscreteTextButton
                                label={MANAGE_BILLING_BTN_LABEL}
                                onClick={() => history.push(`/${Routes.ManageSubscription}`)}
                            />
                        </span>,
                    );
                }
            }
        }

        userSettingsElements.push(...[resetPasswordBtn, logoutButton, deleteAccountButton]);

        if (userDataModified) {
            userSettingsElements.push(...[saveDataButton]);
        }
    }

    // If the user type isn't set, we display nothing in the modal except the user type
    // selection dropdown so the user clearly sees what they have to do.
    if (context.userData && !context.userData?.userType) {
        userSettingsElements = [];
        userSettingsElements.push(...[userDataFields]);
    }

    const modalContentElements = [
        showLoginForm ? (
            <div key={"user-account-modal-firebase-auth-ui"} className={`firebase-auth-wrapper`}>
                <AuthForm />
            </div>
        ) : null,
    ].concat(userSettingsElements);

    let authAccessButtonText;
    if (props.referralCode || props.buttonText) {
        // If passed a buttonText prop, we use that.
        // Else, we are accessing via referral page, we want the button to give
        //      CTA to create account.
        authAccessButtonText = props.buttonText ? props.buttonText : CREATE_ACCOUNT_BUTTON_TEXT;
    } else {
        authAccessButtonText = context.user ? MODAL_BUTTON_ACCOUNT_LABEL : USER_LOG_IN_BUTTON_LABEL;
    }

    const handleOpenAccountModal = () => {
        if (!context.user && !context.userData) {
            // No user in global context, so log as sign in/up event
            GA4Service.logEvent(GA4Event.START_USER_AUTH_FLOW);
            amplitudeService.logEvent(new StartUserAuthFlowEvent());
            history.push({
                pathname: `/${Routes.DesktopMain}`,
                search: props.onSuccessRedirectUrl
                    ? `?${QueryParameters.RedirectSuccess}=${encodeURIComponent(props.onSuccessRedirectUrl)}`
                    : "",
            });
        } else {
            setIsModalOpen(!isModalOpen);
        }
    };

    const accessModalButtonProps = {
        text: authAccessButtonText,
        icon: props.hideUserIcon ? undefined : (
            <StyledUserIcon className={`user-account-icon ${lightMode ? "light" : "dark"}`} />
        ),
        isLink: false,
        handleClick: handleOpenAccountModal,
    };

    const accessModalButton = props.isSpecial ? (
        <SpecialLifeAtButton
            className={`access-modal-button`}
            variant={"red"}
            iconFill={true}
            {...accessModalButtonProps}
        />
    ) : props.buttonInDropdown ? (
        <LifeAtButton {...accessModalButtonProps} className={`access-modal-button`} />
    ) : (
        <LifeAtButtonV2
            onClick={handleOpenAccountModal}
            className={`access-modal-button`}
            size={"small"}
            overrideTextColor={"#FFF"}
            overrideBackgroundColor={Styles.buttonFillBlue}
            disableHover={true}
        >
            {authAccessButtonText}
        </LifeAtButtonV2>
    );

    const closeModalAction = useCallback(() => {
        setModalNotifications([]); // clear notifications
        saveUserData();
        setUserDataModified(false);
        setIsModalOpen(false);
    }, [context.userData]);

    return props.contentsOnly ? (
        <span className={`user-account-modal-contents`}>{modalContentElements}</span>
    ) : (
        <>
            {accessModalButton}

            <LifeAtModal
                key={`user-account-modal-lifeat-modal`}
                title={context.user ? MODAL_HEADER_USER_SETTINGS_LABEL : getAuthModalTitle(showAsCreateAccount)}
                isOpen={isModalOpen}
                closeModal={closeModalAction}
                notifications={modalNotifications}
                preventClose={prevClose}
            >
                {modalContentElements}
            </LifeAtModal>
        </>
    );
}

function getAuthModalTitle(asCreate: boolean) {
    return asCreate ? MODAL_HEADER_SIGN_UP_LABEL : MODAL_HEADER_SIGN_IN_LABEL;
}
